import React from 'react';
import { FaAward, FaCheckCircle, FaGlobe, FaUniversity } from 'react-icons/fa';
import { motion } from 'framer-motion';
import FBR from '../../Images/Logos/FBR.png';
import SECP from '../../Images/Logos/SECP.png';
// Removed QUALIFI, OTHM, and PEARSON imports

const Accreditation = () => {
  const accreditationData = [
    {
      title: 'Nationally Recognized',
      description: 'Our institution is recognized by the relevant national education authorities, ensuring that our programs meet the highest standards of quality and academic excellence.',
      icon: <FaAward className="w-12 h-12 text-[#800000]" />
    },
    {
      title: 'Quality Assured',
      description: 'All our programs undergo rigorous quality assurance processes to ensure they meet both national and international education standards.',
      icon: <FaCheckCircle className="w-12 h-12 text-[#800000]" />
    },
    {
      title: 'International Affiliations',
      description: 'We maintain partnerships with renowned international institutions, providing our students with global exposure and recognition.',
      icon: <FaGlobe className="w-12 h-12 text-[#800000]" />
    },
    {
      title: 'Accredited Programs',
      description: 'Our academic programs are accredited by relevant professional bodies, ensuring they are current, relevant, and valued by employers.',
      icon: <FaUniversity className="w-12 h-12 text-[#800000]" />
    }
  ];

  // Data for the accrediting bodies' logos - updated to remove logos
  const logoData = [
    { name: 'FBR', image: FBR },
    { name: 'SECP', image: SECP },
  ];

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.2,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#800000] to-[#a52a2a] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.h1 
            className="text-4xl md:text-5xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Accreditation & Affiliations
          </motion.h1>
          <motion.p 
            className="text-xl max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Our commitment to excellence is reflected in our accreditations and partnerships with leading educational bodies.
          </motion.p>
        </div>
      </div>

      {/* Accreditation Features */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.5 }}
          >
            {accreditationData.map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
                variants={item}
              >
                <div className="text-center mb-4">
                  <div className="inline-block p-3 bg-red-50 rounded-full">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-center mb-3">{feature.title}</h3>
                <p className="text-gray-600 text-center">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Accreditation Bodies Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-4">Our Accrediting Bodies</h2>
            <div className="w-20 h-1 bg-[#800000] mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-3xl mx-auto">
              We are proud to be accredited and recognized by the following esteemed organizations:
            </p>
          </motion.div>

          {/* MODIFIED SECTION WITH REMOVED LOGOS */}
          <motion.div 
            className="flex flex-wrap justify-center gap-8 px-4"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            {logoData.map((logo, index) => (
              <motion.div 
                key={logo.name}
                className="bg-white p-4 rounded-lg shadow-md flex flex-col items-center justify-center h-40 w-full max-w-[200px] hover:shadow-lg transition-all duration-300"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { 
                    opacity: 1, 
                    y: 0,
                    transition: {
                      duration: 0.6,
                      ease: "easeOut",
                      delay: index * 0.1
                    }
                  }
                }}
                whileHover={{ 
                  y: -5, 
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}
              >
                <div className="flex-grow flex items-center justify-center w-full p-2">
                  <img 
                    src={logo.image} 
                    alt={`${logo.name} logo`} 
                    className="max-h-20 object-contain"
                  />
                </div>
                <div className="text-center mt-2">
                  <div className="font-semibold text-gray-800">{logo.name}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          {/* END OF MODIFIED SECTION */}
        </div>
      </section>

      {/* Why It Matters Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-center mb-8">Why Accreditation Matters</h2>
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-[#800000] mb-2">Quality Education</h3>
                <p className="text-gray-600">
                  Accreditation ensures that our programs meet established standards of quality and that we are accountable for achieving what we set out to do.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-[#800000] mb-2">Recognition</h3>
                <p className="text-gray-600">
                  Degrees and certificates from accredited institutions are recognized by employers, professional associations, and other educational institutions.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-[#800000] mb-2">Continuous Improvement</h3>
                <p className="text-gray-600">
                  The accreditation process encourages continuous self-evaluation and improvement, ensuring our programs remain current and relevant.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Accreditation;